import { DocumentEditorPanel } from '@puglieseweb/shared-react-ui';
import { MarkdownRenderer } from '@/components/docs/MarkdownRenderer';
import { cn } from '@/lib/utils';
import '@/styles/docs-shared.css';

export type EditorMode = 'preview' | 'edit';

interface DocContentEditorProps {
  value: string;
  onChange: (value: string) => void;
  mode: EditorMode;
  placeholder?: string;
  /** Shown in preview when the document has no content yet. */
  emptyHint?: string;
  className?: string;
  /** Relative-link + media resolution scope for the preview renderer. */
  basePath?: string;
  onNavigate?: (path: string) => void;
}

/**
 * The one way document content renders inside a full editor
 * (specs/changes/consolidated-full-editor R2): preview through the shared
 * markdown pipeline (mermaid, GFM alerts, highlighting), editing through the
 * shared TipTap WYSIWYG — never a raw markdown textarea.
 */
export function DocContentEditor({
  value,
  onChange,
  mode,
  placeholder = 'Start writing…',
  emptyHint = 'Nothing here yet — click Edit to start writing.',
  className,
  basePath,
  onNavigate,
}: DocContentEditorProps) {
  return (
    <div
      className={cn(
        'rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 flex flex-col min-h-[400px] overflow-hidden',
        className,
      )}
    >
      {mode === 'preview' ? (
        // docs-page + article pick up the shared doc typography — the same
        // pipeline the book reader renders with.
        <div className="docs-page flex-1 min-h-0 overflow-y-auto p-4 sm:p-6">
          {value.trim() ? (
            <article>
              <MarkdownRenderer content={value} basePath={basePath} onNavigate={onNavigate} />
            </article>
          ) : (
            <p className="text-sm text-slate-400 dark:text-slate-500 italic">{emptyHint}</p>
          )}
        </div>
      ) : (
        <DocumentEditorPanel value={value} onChange={onChange} placeholder={placeholder} />
      )}
    </div>
  );
}
